import { OrderPicklistExtra, PageProps } from '../../components/types'
import { PicklistReturnItemColumn, PicklistItemRecordTotalColumn } from '../../constants/Column'
import Modal from '../../components/Modal'
import Comment from '../../components/Comment'
import Badge from '../../components/Badge'
import { OrderPicklistExtraItem } from '../../components/types'
import usePicklistReturnDetail from '../../hooks/usePicklistReturnDetail'

import DataGrid, { 
  SelectColumn, 
  RowsChangeData, 
  FormatterProps,
  SelectCellFormatter
} from 'react-data-grid'
import { DgtPdOrderPicklist, DgtPdOrderPicklistExtraReturnItem, DgtPdOrderPicklistMovement, OrderType } from '../../models'
import { useEffect, useState, useCallback, useMemo } from 'react'
import ApprovedLabel from '../../components/ApprovedLabel'
import CanceledLabel from '../../components/CanceledLabel'
import { formatNumber, formatDateFromUnix } from '../../utilities/Format'
import CheckboxFormatter from '../../formatters/CheckboxFormatter'
import {
  approveReturnPicklist,
  cancelExtraReturnPicklist,
  getMovementsById,
  getComments,
  postComment,
  updateQtyReturnItem,
  updateWithShrinkQtyReturnItem,
  updateWasteQtyReturnItem,
  updateLotReturnItem,
  updateStoLocReturnItem,
  updateRemarkReturnItem,
  newReturnItem,
  inActiveReturnItem,
  whSplitReturnPicklist,
  activeReturnItem,
  updateReturnReasonCodeReturnItem,
  updateStatusComment
} from '../../states/PicklistAction'
import { DgtPdOrderPicklistExtraReturn } from '../../models'
import { useHistory, Link } from 'react-router-dom'
import { groupBy as rowGrouper } from 'lodash';
import { SiSap } from 'react-icons/si'
import { BsWindowFullscreen } from 'react-icons/bs'
import { AiFillEdit, AiOutlineArrowRight } from 'react-icons/ai'
import { MdContentCut } from 'react-icons/md'

import {
  useForm,
  useFieldArray,
  SubmitHandler,
  FieldArrayPath
} from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import { postReturn, postReturnFG } from '../../apis'

interface ReturnForm {
  headerText: string,
  refDocNo: string,
  items: (DgtPdOrderPicklistExtraReturnItem | null)[]
}

const Label = ({ label, value } : { label: string, value?: string | number }) => 
  <div className='flex'>
    <h6 className="font-light basis-1/3 lg:basis-1/4">{label}</h6>
    <h6 className="font-bold basis-2/3 lg:basis-3/4">{value}</h6>
  </div>
  
const getValidateSchema = (orderType: OrderType) => 
  yup.object({
    headerText: yup.string().optional().max(25),
    refDocNo: yup.string().optional().max(25),
    items: yup.array().of(
      yup.object().shape({
        batchLotNo: orderType === OrderType.REPACK || orderType === OrderType.CLAIM 
          ? yup.string().optional() : yup.string().required(),
        withShQty: yup.number().nullable(true),
        qty: yup.number().nullable(true),
        wasteQty: yup.number().nullable(true),
        returnStorageLocation: yup.string().nullable(true).when('withShQty', {
          is: (val: number) => val > 0,
          then: (schema) => schema.required().notOneOf(['-']),
          otherwise: (schema) => schema.nullable(true),
        }),
        remark: yup.string().nullable(true).max(50, 'ขอไม่เกิน 50 ตัวอักษร')
      })
    ).compact((v) => !v.active)
  })
  

const GoodReverseOrderDetail = ({ title, id, username, readOnly } : PageProps) => {
  const [ openDialogApprove, setOpenDialogApprove ] = useState<boolean>(false)
  const [ openDialogCancel, setOpenDialogCancel ] = useState<boolean>(false)
  const [ openDialogSplitReturn, setOpenDialogSplitReturn ] = useState<boolean>(false)
  const [ movementHistories, setMovementHistories ] = useState<DgtPdOrderPicklistMovement[]>([])
  const [ comments, setComments ] = useState<DgtPdOrderPicklist[]>([])
  const [ loading, setLoading ] = useState<boolean>(false)
  const [ error, setError ] = useState<String>()
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<string>>(() => new Set());

  const history = useHistory()
  
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );

  const [ returnPicklist, mutatePicklist ] = usePicklistReturnDetail(id!)
  const orderInfo: DgtPdOrderPicklist = useMemo(() => returnPicklist?.orderInfo ?
    JSON.parse(returnPicklist?.orderInfo) : {}, [ returnPicklist ])

  const schema = getValidateSchema(orderInfo.orderType as OrderType)
  const {
    control, handleSubmit,
    register,
    reset,
    formState: { errors },
    getValues
  } = useForm<ReturnForm>({
    defaultValues: {
      items: [],
      headerText: '',
      refDocNo: ''
    },
    resolver: yupResolver(schema)
  })

  const { replace, fields } = useFieldArray<ReturnForm, FieldArrayPath<ReturnForm>, '_id'>({
    control, name: 'items', keyName: '_id'
  })

  const onApproved = async () => {
    setError(undefined)
    setLoading(true)
    const { headerText, items, refDocNo } = getValues()

    const activeItems = items.filter(itm => itm?.active !== false)
    
    const orderNo = activeItems[0]?.orderId!
    
    const isFGReturn = orderInfo.orderType === OrderType.REPACK || orderInfo.orderType === OrderType.CLAIM
    const response = isFGReturn ? await postReturnFG(
      orderInfo.orderType as OrderType, orderNo, headerText, activeItems as DgtPdOrderPicklistExtraReturnItem[], refDocNo) : await postReturn(orderNo, headerText, activeItems as DgtPdOrderPicklistExtraReturnItem[])
    
    if (response.success && response.docNo && response.docYear) {
      const updated = await approveReturnPicklist(
        returnPicklist as OrderPicklistExtra, 
        username!, 
        headerText, 
        response.docNo,
        response.docYear,
        isFGReturn ? refDocNo : undefined)
      setOpenDialogApprove(false)
      const updatedReturn = {
        ...updated, extraReturnItems: updated.extraReturnItems.items
      }
      mutatePicklist(updatedReturn)
    } else {
      setError(response.message)
    }
    setLoading(false)
  }

  const onCanceled = async () => {
    await cancelExtraReturnPicklist(returnPicklist as OrderPicklistExtra, username!)
    history.goBack()
  }

  const onConfirmSplit = async () => {
    setLoading(true)

    const newReturnId = await whSplitReturnPicklist(
      returnPicklist!, 
      Array.from(selectedRows)
    )
    
    onPostComment(`มีการแยกใบคืนใบใหม่คือ ${newReturnId.slice(newReturnId.length - 8)} (แยกมาจาก ${returnPicklist?.id.slice(returnPicklist?.id.length - 8)})`, true)

    setSelectedRows(new Set())
    setLoading(false)
    setOpenDialogSplitReturn(false)
    // Note - mutate for refresh latest movement after split
    const remainItems = returnPicklist?.extraReturnItems?.filter(itm => !selectedRows.has(itm?.id!))
    const updatedReturn = {
      ...returnPicklist!, extraReturnItems: remainItems
    }
    mutatePicklist(updatedReturn)
  }

  const renderLabel = (returnPicklist: DgtPdOrderPicklistExtraReturn) => {
    if (!returnPicklist) return null
    
    if (returnPicklist.canceledDateTime) {
      return <CanceledLabel 
        message={`ยกเลิกคืนโดย ${returnPicklist.canceledBy} / ${formatDateFromUnix(returnPicklist.canceledDateTime)}`}
      />
    }

    if (returnPicklist.approvedDateTime) {
      return <div>
        <ApprovedLabel 
          message={`อนุมัติคืนโดย ${returnPicklist.approvedBy} / ${formatDateFromUnix(returnPicklist.approvedDateTime)}`}
        />
        {returnPicklist?.documentNo && <h6>Mat Doc. {returnPicklist.documentYear}/{returnPicklist.documentNo}</h6>}
      </div>
    }
  }

  useEffect(() => {
    const fetchMovements = async (
      orderId : string, 
      extraItems: (DgtPdOrderPicklistExtraReturnItem | null)[]
      ) => {
      const materialCodes = extraItems.map(itm => itm?.materialCode)
      const mvs = await getMovementsById(orderId, '')
      const filtered = mvs.filter(mv => materialCodes.includes(mv.materialCode))
      setMovementHistories(filtered)
      // setExpandedGroupIds(new Set(filtered.map(itm => itm.materialCode)))
    }
    const fetchComments = async (orderId: string) => {
      const comments = await getComments(orderId)
      setComments(comments)
    }
    if (returnPicklist) {
      fetchMovements(returnPicklist.orderId, returnPicklist.extraReturnItems || [])
      fetchComments(returnPicklist.orderId)
    }
  }, [ returnPicklist ])

  const onPostComment = useCallback(async (comment: string, commentedBySystem?: boolean) => {
    const commentResponse = await postComment(
      returnPicklist?.orderId!,  
      comment, 
      commentedBySystem ? 'System' : username!
    )
    setComments([...comments, commentResponse])
  }, [ returnPicklist?.orderId, username, comments ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
    setComments(comments.map(c => {
      if (c.sk === skCommentId) {
        return updatedComment
      }
      return c
    }
    ))
  }, [ comments ])

  const onRowsChange = async (rows: (OrderPicklistExtraItem | null)[], 
    changedInfo: RowsChangeData<(OrderPicklistExtraItem | null)>) => {

    const changedRowIdx = changedInfo.indexes[0]
    let changedRows = [...rows]

    const row = changedRows[changedRowIdx]
    const originalRow = returnPicklist?.extraReturnItems?.find(itm => itm?.id === row?.id)

    let updatedItem : (OrderPicklistExtraItem | null) = null;

    if (changedInfo.column.key === 'qty') {
      updatedItem = await updateQtyReturnItem(row?.id!, row?.qty || 0, row?._version || 1)
      onPostComment(`[${row?.materialCode!}] มีการแก้ไขข้อมูล คืนดี จาก ${originalRow?.qty} เป็น ${row?.qty} โดย ${username}`, true)
    } else if (changedInfo.column.key === 'withShQty') {
      updatedItem = await updateWithShrinkQtyReturnItem(row?.id!, row?.withShQty || 0, row?._version || 1)
      onPostComment(`[${row?.materialCode!}] มีการแก้ไขข้อมูล คืนดีติดSH/STK จาก ${originalRow?.withShQty} เป็น ${row?.withShQty} โดย ${username}`, true)
    } else if (changedInfo.column.key === 'wasteQty') {
      updatedItem = await updateWasteQtyReturnItem(row?.id!, row?.wasteQty || 0, row?._version || 1)
      onPostComment(`[${row?.materialCode!}] มีการแก้ไขข้อมูล คืนเสีย จาก ${originalRow?.wasteQty} เป็น ${row?.wasteQty} โดย ${username}`, true)
    } else if (changedInfo.column.key === 'returnReasonCode') {
      updatedItem = await updateReturnReasonCodeReturnItem(row?.id!, row?.returnReasonCode!, row?.returnReasonDescription!, row?._version || 1)
      onPostComment(`[${row?.materialCode!}] มีการแก้ไขข้อมูล Return Reason จาก ${originalRow?.returnReasonCode} เป็น ${row?.returnReasonCode} โดย ${username}`, true)
    } else if (changedInfo.column.key === 'batchLotNo') {
      updatedItem = await updateLotReturnItem(row?.id!, row?.batchLotNo || '', row?._version || 1)
      onPostComment(`[${row?.materialCode!}] มีการแก้ไขข้อมูล Batch Lot จาก ${originalRow?.batchLotNo} เป็น ${row?.batchLotNo} โดย ${username}`, true)
    } else if (changedInfo.column.key === 'returnStorageLocation') {
      updatedItem = await updateStoLocReturnItem(row?.id!, row?.returnStorageLocation || '-', row?._version || 1)
    } else if (changedInfo.column.key === 'remark') {
      updatedItem = await updateRemarkReturnItem(row?.id!, row?.remark!, row?._version || 1)
    } else if (changedInfo.column.key === 'delete') {
      if (row?.active) {
        updatedItem = await activeReturnItem(row?.id!, row?._version || 1)
      } else {
        updatedItem = await inActiveReturnItem(row?.id!, row?._version || 1)
      }
    } else if (changedInfo.column.key === 'copy') {
      if (row?.copyToNormal) {
        const newRow = { ...row,
          id: `${Math.floor(Date.now() / 1000)}`,
          wasteQty: 0,
          qty: 1, withShQty: 0,
          remark: undefined,
          returnStorageLocation: undefined
        } as DgtPdOrderPicklistExtraReturnItem
        const inserted = await newReturnItem(row?.extraReturnId!, newRow, 1, 0, 0)
        changedRows = [...changedRows, inserted]
      }
      if (row?.copyToWithSh) {
        const newRow = { ...row,
          id: `${Math.floor(Date.now() / 1000)}`,
          wasteQty: 0,
          qty: 0, withShQty: 1,
          remark: undefined,
          returnStorageLocation: undefined
        } as DgtPdOrderPicklistExtraReturnItem
        const inserted = await newReturnItem(row?.extraReturnId!, newRow, 0, 1, 0)
        changedRows = [...changedRows, inserted]
      }
      if (row?.copyToWaste) {
        const newRow = { ...row,
          id: `${Math.floor(Date.now() / 1000)}`,
          wasteQty: 1,
          qty: 0, withShQty: 0,
          remark: undefined,
          returnStorageLocation: (orderInfo.orderType === OrderType.CLAIM || orderInfo.orderType === OrderType.REPACK) ? row.returnStorageLocation : (row?.orderId?.startsWith('9') ? 'LREJ':'RMQH')
        } as DgtPdOrderPicklistExtraReturnItem
        const inserted = await newReturnItem(row?.extraReturnId!, newRow, 0, 0, 1)
        changedRows = [...changedRows, inserted]
      }
    }

    replace(updatedItem ? 
      changedRows.map(row => updatedItem && row?.id === updatedItem.id ? updatedItem : row) : changedRows)
  }

  useEffect(() => {
    reset({
      items: (returnPicklist?.extraReturnItems || []).map(ret => {
        if (ret?.wasteQty! > 0) {
          if (ret?.orderId?.startsWith('9') &&
              orderInfo.orderType !== OrderType.CLAIM && orderInfo.orderType !== OrderType.REPACK) 
              return { ...ret, returnStorageLocation: "LREJ" } 
          if (ret?.orderId?.startsWith('8')) return { ...ret, returnStorageLocation: "RMQH" } 
        }
        return { ...ret }
      }),
      headerText:  returnPicklist?.remark || '',
      refDocNo: returnPicklist?.kiNo || ''
    })
  }, [ reset, returnPicklist, orderInfo ])

  const sortedByCodeItems = useMemo(() => {
    return [...fields].map(itm => ({
      ...itm,
      otherQty: movementHistories.find(
        mv => mv.returnRefId === id && 
        mv.materialCode === itm.materialCode && 
        mv.batchLotNo === itm.batchLotNo)?.otherQty || undefined
    })).sort((a, b) => 
      (Number(b.active === null ? true : b.active) - Number(a.active === null ? true : a.active)) ||
      (a?.materialCode || '').localeCompare(b?.materialCode || '') ||
      (b?.qty! - a?.qty!) ||
      (b?.withShQty! - a?.withShQty!) ||
      (b?.wasteQty! - a?.wasteQty!)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ movementHistories, fields ])

  const activeItems = useMemo(() => sortedByCodeItems.filter(itm => itm.active !== false), [ sortedByCodeItems ])

  const columnItemReadOnly = PicklistReturnItemColumn.map(col => {
    return { ...col, editable: false }
  }).filter(col => col.key !== 'delete' && col.key !== 'copy' && col.key !== 'manualSLOC')

  const onSubmitReturn: SubmitHandler<ReturnForm> = async (data) => {
    setOpenDialogApprove(true)
  }

  const columns = useMemo(() => {
    if (readOnly) {
      return columnItemReadOnly
    }
    if (orderInfo.orderType === OrderType.REPACK || orderInfo.orderType === OrderType.CLAIM) {
      // remove column key equal 'batchLotNo' and add formatter for returnStorageLocation
      return PicklistReturnItemColumn.filter(col => col.key !== 'batchLotNo')
        .map(col => {
          if (col.key === 'returnStorageLocation') {
            return { 
              ...col,
              formatter: ({ row, column }: FormatterProps<OrderPicklistExtraItem | null>) => {
                if (row?.withShQty! > 0 || row?.manualLocation) {
                  if (row?.returnStorageLocation && row.returnStorageLocation !== '-') {
                    return <div className='flex items-center justify-center h-full gap-2'>
                      <span className='font-bold '>{row?.returnStorageLocation}</span>
                      {column.editable ? <AiFillEdit className=' text-[#007bff] cursor-pointer' /> : null}
                    </div>
                  }
                  return <div className='italic work-center-edit '>เลือก Location</div>
                }
                return row?.manualLocation ? <></> : <div className='italic text-muted'>{row?.returnStorageLocation ? row?.returnStorageLocation : 'Loc เดิม'}</div>
              }
            }
          }
          if (col.key === 'manualSLOC') {
            return {
              ...col,
              formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
                const { row, isCellSelected } = props
                if ((row?.qty! > 0 || row?.withShQty! > 0 || row?.wasteQty! > 0) && row?.active) {
                  const isChecked = row?.manualLocation || false
                  return <SelectCellFormatter
                    value={isChecked}
                    onChange={() => {
                      const currentRow = { 
                        ...row,
                        manualLocation : !row?.manualLocation
                      } as OrderPicklistExtraItem | null
                      props.onRowChange(currentRow);
                    }}
                    isCellSelected={isCellSelected}
                  />
                }
                return <></>
              }
            }
          }
          return col
        })
    }
    return PicklistReturnItemColumn
  }, [ readOnly, columnItemReadOnly, orderInfo ])

  return <div className="page-container">
    <div className='flex items-center justify-between gap-2'>
      <div className='flex items-center gap-1'>
        <h3 className={`my-3`}>{`${title} Order : ${returnPicklist?.orderId}`}</h3>
        {orderInfo.orderType && orderInfo.orderType === OrderType.REPACK && <h6>
          <Badge variant={` text-white bg-blue-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge>
        </h6>}
        {orderInfo.orderType && orderInfo.orderType === OrderType.CLAIM && <h6>
          <Badge variant={` text-white bg-red-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge></h6>}
      </div>
      {renderLabel(returnPicklist!)}
    </div>
    <div className='flex flex-wrap gap-1 mb-4'>
      {orderInfo.infoRefPkg && <Badge variant='product-info'>{orderInfo.infoRefPkg}</Badge>}
      {orderInfo.infoPallet && <Badge variant='product-info'>{`Pallet : ${orderInfo.infoPallet}`}</Badge>}
      {orderInfo.infoPacking && <Badge variant='product-info'>{`Packing : ${orderInfo.infoPacking}`}</Badge>}
    </div>
    <form onSubmit={handleSubmit(onSubmitReturn)}>
      <div className='grid grid-flow-row gap-1 md:grid-flow-col md:grid-rows-3'>
        <Label label="Mat Code" value={orderInfo.materialCode} />
        <Label label="Mat Description" value={orderInfo.materialDescription} />
        <Label label="Order Qty" value={`${formatNumber(orderInfo.orderQty!)} ${orderInfo.materialUnit || ''}`} />
        <Label label="Batch No" value={orderInfo.batchLotNo!} />
        <div className='flex items-center'>
          <h6 className="font-light basis-1/3 lg:basis-1/4">Header Text (จำกัด 25 ตัวอักษร)</h6>
          <input placeholder='กรอก Header Text'
            maxLength={25}
            disabled={readOnly}
            className={`input-form-text basis-1/3 lg:basis-2/4`}
            {...register('headerText')}
          />
          {errors.headerText ? <small className='pl-3 italic text-red-500'>{errors.headerText.message}</small> : null}
        </div>
        <div className='flex items-center'>
          <h6 className="font-light basis-1/3 lg:basis-1/4">Ref Doc No.</h6>
          <input placeholder='กรอก Ref Doc No.'
            maxLength={25}
            disabled={readOnly}
            className={`input-form-text basis-1/3 lg:basis-2/4`}
            {...register('refDocNo')}
          />
          {errors.refDocNo ? <small className='pl-3 italic text-red-500'>{errors.refDocNo.message}</small> : null}
        </div>
      </div>
      <div className='flex items-end justify-between mb-2'>
        <div>
          <small>รายการคืน {activeItems.length} รายการ</small>
          {readOnly ? null : <button type='button' className='gap-1 ml-3 btn-primary' onClick={() => setOpenDialogSplitReturn(true)}>
            <MdContentCut size={20}/>
            แยกใบคืน
          </button>}
        </div>
        {readOnly ? null : <div className='space-x-2 '>
          <button type='submit' className=' btn-success' >กดเพื่ออนุมัติคืน</button>
          <button type='button' className=' btn-outline-danger'
            onClick={() => setOpenDialogCancel(true)}>ยกเลิกการคืน</button>
        </div>}
      </div>
      {errors.items ? <div className='w-full text-right '><small className='italic text-red-500 '>กรุณาเลือก Location/กรอก Lot/กรอก Note ไม่เกิน 50 ตัว</small></div> : null}
      <DataGrid
        className='mb-3'
        style={{ height: "100%", contain: "none" }}
        columns={columns}
        rows={sortedByCodeItems}
        defaultColumnOptions={{ resizable: true }}
        onRowsChange={onRowsChange}
        headerRowHeight={50}
        components={{ checkboxFormatter: CheckboxFormatter }}
        rowClass={row => row?.active === false ? ' deprecated-item-row bg-gray-200':''}
      />
    </form>
    <small>รายละเอียดการใช้ดีเสีย</small>
    <DataGrid
      className='mt-2 mb-3'
      style={{ height: "100%", contain: "none" }}
      columns={[...PicklistItemRecordTotalColumn]}
      rows={movementHistories}
      rowKeyGetter={(row) => row.id}
      groupBy={['materialCode']}
      rowGrouper={rowGrouper}
      expandedGroupIds={expandedGroupIds}
      onExpandedGroupIdsChange={setExpandedGroupIds}
      defaultColumnOptions={{ resizable: true }}
    />
    <Link to={`/good-issue-order/${orderInfo.orderType?.toLocaleLowerCase()}/${returnPicklist?.orderId}`} target="_blank"
      className='float-right '>
      <h6 className='text-blue-600 underline '>กดดูรายละเอียดใบเบิก</h6>
    </Link>
    <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
    <Modal show={openDialogApprove} cancelText='ยกเลิก'
      onHide={() => setOpenDialogApprove(false)}
      okText="ยืนยัน"
      okTheme='success'
      okAction={onApproved}
      title={`คุณกำลังอนุมัติคืนของ ORDER ${returnPicklist?.orderId} / ${getValues('headerText')} / ${getValues('refDocNo')}`}
      maxWidth='max-w-8xl'
      loading={loading}
    >
      <div className='space-y-3'>
        {orderInfo.orderType && orderInfo.orderType === OrderType.REPACK && <h6 className='text-center '>
          <Badge variant={` text-white bg-blue-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge>
        </h6>}
        {orderInfo.orderType && orderInfo.orderType === OrderType.CLAIM && <h6 className='text-center '>
          <Badge variant={` text-white bg-red-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge></h6>}
        <h5 className='flex items-center justify-center gap-3 text-center '>ข้อมูลการคืนจะถูก Post ไปยัง SAP อัตโนมัติ</h5>
        <div className='flex items-center justify-center gap-4 '>
          <BsWindowFullscreen size={40} className='' />
          <AiOutlineArrowRight size={20} className='' />
          <SiSap size={48} className='text-blue-500 ' />
        </div>
        <p>มีรายการคืนมาจาก PD {activeItems.length} รายการ</p>
        <DataGrid
          style={{ height: 'fit-content' }}
          columns={columnItemReadOnly}
          rows={activeItems}
          defaultColumnOptions={{ resizable: true }}
          headerRowHeight={50}
        />
        <h5 className='text-center '>คุณต้องการยืนยันรับคืนใช่หรือไม่ ?</h5>
        {error ? <p className='text-center text-red-500'>เกิดข้อผิดพลาด : {error}</p> : null}
      </div>
    </Modal>
    <Modal show={openDialogCancel} cancelText='ปิด'
      onHide={() => setOpenDialogCancel(false)}
      okText="ยกเลิกการคืน"
      okTheme='danger'
      okAction={onCanceled}
      title={`คุณกำลังยกเลิกอนุมัติคืนของ ORDER ${returnPicklist?.orderId}`}
    >
      <h6>คุณต้องการยืนยันยกเลิกใช่หรือไม่ ?</h6>
    </Modal>
    <Modal show={openDialogSplitReturn} cancelText='ยกเลิก'
      onHide={() => setOpenDialogSplitReturn(false)}
      okText="สร้างใบคืนใหม่"
      okTheme='info'
      okAction={onConfirmSplit}
      title={`แยกใบคืนของ PD Order : ${returnPicklist?.orderId} - เลือกรายการที่ต้องการแยกใบคืน`}
      maxWidth='max-w-5xl'
      loading={loading}
      okDisable={selectedRows.size === 0 || selectedRows.size === activeItems.length}
    >
      <div className='space-y-2 '>
         <div className='flex gap-2'>
            <p className='italic text-red-500 underline '>คำแนะนำ</p>
            <div>
            <p className='italic text-red-500'>1. เลือกอย่างน้อย 1 รายการ แต่ไม่สามารถเลือกทั้งหมดได้</p>
            <p className='italic text-red-500'>2. แยกใบคืนแล้ว ไม่สามารถนำกลับมาได้ กรุณาตรวจสอบให้ดีก่อนกดสร้างใบคืนใหม่</p>
            </div>
         </div>
         
         <DataGrid
          columns={[ 
            SelectColumn, 
            ...PicklistReturnItemColumn.slice(0, -4).map(col => ({ ...col, editable: false }))
          ]}
          rows={activeItems}
          headerRowHeight={50}
          style={{ height: 'fit-content' }}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          rowKeyGetter={row => row?.id || ''}
          components={{ checkboxFormatter: CheckboxFormatter }}
         />
         <p className='text-sm italic text-right '>*หลังจากสร้างคืนใหม่แล้ว สามารถค้นหาจากเลข PD Order ได้ที่หน้าแรก</p>
      </div>
    </Modal>
  </div>
}

export default GoodReverseOrderDetail